<template>
  <!-- A custom landing page replaces the whole built-in page, not part of it:
       an uploaded document is a complete marketing page, and framing it inside
       the shell's own hero would fight it for the same screen. -->
  <CustomLanding
    v-if="customLandingUrl && !formOpen"
    :html-url="customLandingUrl"
    :app-name="tenantName"
    :primary="primaryColour"
    :label="buttonLabel"
    :loading="loading"
    :error-message="errorMessage ?? ''"
    :localize-boer="localizeBoerLanding"
    @signin="onLandingSignin"
  />

  <!-- One centred card, not a two-column split. The left column of the old
       layout held a logo, a name and (usually) nothing else, because
       welcomeTitle/welcomeText/tiles are all optional — so the page read as
       two lonely boxes whenever a tenant had not filled the copy in. This
       composition looks deliberate with or without it. -->
  <PreAuthPage v-else class="login-page" wide card-class="login-card">
    <section class="sh-card__head login-page__hero">
      <LogoTile :branding="branding" :alt="tenantName" :size="56" />
      <h1 class="login-hero__name">{{ tenantName }}</h1>
      <p v-if="welcomeTitle" class="login-hero__welcome-title">{{ welcomeTitle }}</p>
      <p v-if="welcomeText" class="login-hero__welcome-text sh-card__subtitle">{{ welcomeText }}</p>
    </section>

    <!-- Pre-auth preview of what the app contains — names only, never a nav. -->
    <div v-if="hintTiles.length" class="login-hero__hints">
      <div v-for="tile in hintTiles" :key="tile.route" class="login-hero__hint">
        <span class="login-hero__hint-icon"><v-icon size="16" color="primary">{{ tile.icon }}</v-icon></span>
        <span class="login-hero__hint-label">{{ tile.title }}</span>
      </div>
    </div>

    <!-- `role="alert"` so a screen reader announces the failure the moment
         the card swaps back from the callback spinner. The raw Auth0 text
         stays in the title for whoever has to debug it. -->
    <p v-if="errorMessage" class="login-signin__error sh-card__error" role="alert" :title="errorMessage">
      <v-icon size="15">error_outline</v-icon>
      <span>{{ friendlyError }}</span>
    </p>
    <!-- PAC-1387: the raw text, for whoever has to debug it — folded away so
         a visitor never reads "GET /… failed: 403" as the message. -->
    <details v-if="showErrorDetail" class="login-signin__detail">
      <summary>{{ t("auth.errors.details") }}</summary>
      <code>{{ errorMessage }}</code>
    </details>

    <!-- R4: mode "both" and "identity" draw the Better Auth form; "both" also
         keeps a way back to the Auth0 page. Mode "auth0" is unchanged. -->
    <template v-if="useIdentityForm">
      <SignInForm
        :providers="providers"
        :sign-in-label="formSignInLabel"
        :allow-sign-up="allowSignUp"
        @signed-in="onIdentitySignedIn"
      />
      <v-btn
        v-if="identityMode === 'both'"
        data-test="previous-sign-in"
        class="login-signin__switch"
        variant="text"
        size="small"
        :loading="loading"
        @click="signIn()"
        >{{ t("auth.identity.previousPage") }}</v-btn>
    </template>

    <template v-else>
      <v-btn
        ref="signInButton"
        class="login-signin__button"
        color="primary"
        size="large"
        block
        :loading="loading"
        @click="signIn()"
        >{{ errorMessage ? t("auth.tryAgain") : buttonLabel }}</v-btn>

      <!-- Only after a failure: the commonest cause of "not allowed" is the
           wrong Auth0 account, and a plain retry would silently reuse it. -->
      <v-btn
        v-if="errorMessage"
        class="login-signin__switch"
        variant="text"
        size="small"
        :disabled="loading"
        @click="signIn('login')"
        >{{ t("auth.differentAccount") }}</v-btn>
    </template>

    <!-- PAC-1388: a WMS front door's own sign-up page. Boer and every other
         app keep Auth0's sign-up (offersCompanySignup is false there). -->
    <p v-if="companySignup" class="login-signup">
      {{ t("signup.noAccount") }}
      <a :href="signupPath" class="login-signup__link" @click.prevent="openSignup">{{ t("signup.createCompanyAccount") }}</a>
    </p>
  </PreAuthPage>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useSessionStore } from "@/stores/session";
import { hasAuthErrorDetail, humaniseAuthError } from "@/auth/authErrors";
import { identityProviders, signInLabel } from "@/tenant/loginLinks";
import { signInWithProvider, type SocialProvider } from "@/auth/identity";
import SignInForm from "@/shell/SignInForm.vue";
import LogoTile from "@/shell/LogoTile.vue";
import CustomLanding from "@/shell/CustomLanding.vue";
import PreAuthPage from "@/shell/PreAuthPage.vue";
import { isBoerMarketingLanding } from "@/landing/isBoerLanding";
import type { TenantHomeTile } from "@/tenant/types";
import { goTo, offersCompanySignup, SIGNUP_PATH } from "@/signup/companySignup";

/** Matches `applyBranding()`'s own guard — the colour is handed to the landing
 *  document as a query param, so it must never be free text. */
const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;
const DEFAULT_PRIMARY = "#649ECE";

/** "What's inside" hint row shows at most this many tiles — a preview, not a full nav. */
const MAX_HINT_TILES = 4;

export default defineComponent({
  name: "Login",
  components: { LogoTile, CustomLanding, PreAuthPage, SignInForm },
  setup() {
    const { t } = useI18n();
    const session = useSessionStore();
    const loading = ref(false);
    const errorMessage = computed(() => session.errorMessage);
    const friendlyError = computed(() => humaniseAuthError(errorMessage.value));
    const showErrorDetail = computed(() => hasAuthErrorDetail(errorMessage.value));

    const tenantName = computed(() => session.tenant?.name || "Metaship");
    const branding = computed(() => session.tenant?.branding ?? null);
    const homePage = computed(() => session.tenant?.homePage ?? null);

    // The backend degrades a custom page with no document back to "default",
    // so mode alone is enough — no separate null check on the URL.
    const loginPage = computed(() => session.tenant?.loginPage ?? null);
    const customLandingUrl = computed<string | null>(() =>
      loginPage.value?.mode === "custom" ? loginPage.value.htmlUrl : null,
    );
    const localizeBoerLanding = computed(() =>
      isBoerMarketingLanding({
        slug: session.tenant?.slug,
        htmlUrl: customLandingUrl.value,
        hostname: session.host || window.location.hostname,
      }),
    );
    const buttonLabel = computed(() => {
      const custom = loginPage.value?.signInLabel?.trim();
      return custom ? signInLabel(loginPage.value) : t("auth.signIn");
    });
    const primaryColour = computed<string>(() => {
      const colour = branding.value?.colours?.primary;
      return colour && HEX_COLOUR.test(colour) ? colour : DEFAULT_PRIMARY;
    });

    // Only shown when it says something the app name doesn't already say.
    const welcomeTitle = computed(() => {
      const title = homePage.value?.welcomeTitle?.trim();
      if (!title) return null;
      return title.toLowerCase() === tenantName.value.trim().toLowerCase() ? null : title;
    });
    const welcomeText = computed(() => homePage.value?.welcomeText || null);

    // Pre-auth, so there's no resolved-modules/allowedItems permission model
    // to check yet (that's post-sign-in, see AppHome.vue) — this is purely a
    // marketing preview of what the tenant configured, names only, not a nav.
    //
    // A tile whose title IS the app name is dropped, same rule as
    // `welcomeTitle` above: App Home seeds one tile per module from the
    // module's own name, so a single-module app (crm.metaship.ai) produced a
    // "CRM" hint under a "CRM" heading under a CRM logo — three ways of saying
    // one word. When nothing survives, the whole row disappears.
    const hintTiles = computed<TenantHomeTile[]>(() => {
      if (!homePage.value?.enabled) return [];
      const appName = tenantName.value.trim().toLowerCase();
      return (homePage.value.tiles ?? [])
        .filter((tile) => tile.title.trim().toLowerCase() !== appName)
        .slice(0, MAX_HINT_TILES);
    });

    /** `prompt: "login"` makes Auth0 ask for credentials again even while its
     *  own SSO cookie is alive — the "different account" path. */
    async function signIn(prompt?: "login"): Promise<void> {
      loading.value = true;
      try {
        await session.beginLogin(prompt);
      } finally {
        loading.value = false;
      }
    }

    const companySignup = computed(() => offersCompanySignup(session.tenant));

    const identityMode = computed(() => session.identityMode);
    const useIdentityForm = computed(() => identityMode.value !== "auth0");
    const providers = computed(() => identityProviders(loginPage.value));
    // Only a configured label; the form has its own translated default.
    const formSignInLabel = computed(() => (loginPage.value?.signInLabel?.trim() ? signInLabel(loginPage.value) : ""));
    // A WMS front door has its own sign-up page (companySignup), so no second one here.
    const allowSignUp = computed(() => session.tenant?.selfServeSignup === true && !companySignup.value);
    // A custom landing page hands over to the built-in page, which holds the form.
    const formOpen = ref(false);

    async function onIdentitySignedIn(): Promise<void> {
      formOpen.value = false;
      try {
        await session.completeIdentitySignIn();
      } catch (e) {
        if (session.status === "signed-out") session.fail(e instanceof Error ? e.message : "Authentication failed");
      }
    }

    /** The landing page's sign-in drop-in. `provider` goes straight to Google or Microsoft when the tenant offers it. */
    async function onLandingSignin(provider?: SocialProvider): Promise<void> {
      if (!useIdentityForm.value) return signIn();
      if (provider && providers.value.includes(provider)) {
        loading.value = true;
        try {
          const error = await signInWithProvider(provider, "/");
          if (error) session.fail(error.message || error.code);
        } finally {
          loading.value = false;
        }
        return;
      }
      formOpen.value = true;
    }
    const openSignup = () => goTo(SIGNUP_PATH);

    // Enter signs in. The button is the page's only control, so it takes
    // focus on arrival instead of making the visitor reach for the mouse.
    const signInButton = ref<{ $el?: HTMLElement } | null>(null);
    onMounted(() => signInButton.value?.$el?.focus?.());

    return {
      t,
      tenantName,
      branding,
      welcomeTitle,
      welcomeText,
      hintTiles,
      loading,
      errorMessage,
      friendlyError,
      showErrorDetail,
      signIn,
      signInButton,
      customLandingUrl,
      localizeBoerLanding,
      primaryColour,
      buttonLabel,
      companySignup,
      openSignup,
      identityMode,
      useIdentityForm,
      providers,
      formSignInLabel,
      allowSignUp,
      formOpen,
      onIdentitySignedIn,
      onLandingSignin,
      signupPath: SIGNUP_PATH,
    };
  },
});
</script>

<style scoped>
/* Page frame, glows, card and error surface come from src/styles/standalone.css
   (`.sh-page`, `.sh-card`) — every full-page shell view shares them. Only the
   sign-in page's own composition lives here. */
.login-card {
  display: flex;
  flex-direction: column;
}

.login-signin__detail {
  margin: -4px 0 12px;
  font-size: 12px;
  color: var(--sh-text-60);
}
.login-signin__detail summary {
  cursor: pointer;
}
.login-signin__detail code {
  display: block;
  margin-top: 4px;
  word-break: break-word;
  white-space: pre-wrap;
}

.login-hero__name {
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0;
  color: var(--sh-text);
}
.login-hero__welcome-title {
  font-size: 13.5px;
  font-weight: 600;
  margin: -4px 0 0;
  color: var(--sh-text);
}
.login-hero__welcome-text {
  margin: 0;
}

/* Rows, not chips: a chip row wrapped to one lonely pill when a tenant had a
   single tile, which is the common case for a one-module app. */
.login-hero__hints {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0 0 20px;
  padding: 10px 0;
  border-top: 1px solid var(--sh-hairline);
  border-bottom: 1px solid var(--sh-hairline);
}
.login-hero__hint {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 4px;
}
.login-hero__hint-icon {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(var(--v-theme-primary), 0.12);
}
.login-hero__hint-label {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--sh-text);
}

.login-signin__button {
  font-weight: 600;
}
.login-signup {
  margin: 16px 0 0;
  text-align: center;
  font-size: 12.5px;
  color: var(--sh-text-60);
}
.login-signup__link {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
  text-decoration: none;
}
.login-signup__link:hover {
  text-decoration: underline;
}
.login-signin__switch {
  align-self: center;
  margin-top: 8px;
  font-size: 12px;
  color: var(--sh-text-60);
}

@media (max-width: 480px) {
  .sh-card {
    padding: 22px 18px;
  }
}
</style>
